/**
 * QADialog — Phase 2E trainer-side searchable Q&A knowledge base with
 * reply + resolve actions.
 */
import { useEffect, useState, useCallback } from 'react';
import { Search, CheckCircle2, CornerDownRight } from 'lucide-react';
import { toast } from 'sonner';
import { useApi } from '../../hooks/useApi';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';

export default function QADialog({ onClose }) {
  const { get, post } = useApi();
  const [search, setSearch] = useState('');
  const [unresolvedOnly, setUnresolvedOnly] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [replyTo, setReplyTo] = useState(null); // question_id
  const [replyText, setReplyText] = useState('');

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (search.trim()) p.set('search', search.trim());
    if (unresolvedOnly) p.set('unresolved_only', 'true');
    get(`/learning-qa?${p}`)
      .then(r => setQuestions(r.questions || []))
      .catch(() => {});
  }, [get, search, unresolvedOnly]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const sendReply = async (qid) => {
    if (!replyText.trim()) return;
    try {
      await post(`/learning-qa/${qid}/reply`, { text: replyText.trim() });
      setReplyText('');
      setReplyTo(null);
      toast.success('Reply sent — trainee notified');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reply failed'); }
  };

  const toggleResolve = async (qid) => {
    try {
      await post(`/learning-qa/${qid}/resolve`);
      load();
    } catch { toast.error('Failed'); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="qa-dialog">
        <DialogHeader>
          <DialogTitle>Learning Q&A</DialogTitle>
          <DialogDescription>Questions from agents across all learning assignments — searchable knowledge base.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2 items-center">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input className="pl-8" placeholder="Search questions, answers, trainees…"
                   value={search} onChange={e => setSearch(e.target.value)}
                   data-testid="qa-search-input" />
          </div>
          <Button size="sm" variant={unresolvedOnly ? 'default' : 'outline'}
                  onClick={() => setUnresolvedOnly(v => !v)} data-testid="qa-unresolved-toggle">
            Unresolved
          </Button>
        </div>
        <div className="divide-y" data-testid="qa-dialog-list">
          {questions.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">No questions found.</p>}
          {questions.map(q => (
            <div key={q.question_id} className="py-3" data-testid={`qa-thread-${q.question_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">{q.trainee_name}</span>
                    {' '}on <span className="italic">{q.assignment_title}</span>
                    {' '}· {new Date(q.created_at).toLocaleString()}
                  </p>
                  <p className="text-sm mt-1">{q.text}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {q.resolved
                    ? <Badge className="bg-emerald-100 text-emerald-800 border-0">Resolved</Badge>
                    : <Badge className="bg-amber-100 text-amber-800 border-0">Open</Badge>}
                  <Button size="sm" variant="ghost" onClick={() => toggleResolve(q.question_id)}
                          data-testid={`qa-resolve-${q.question_id}`}>
                    <CheckCircle2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              {(q.replies || []).map(r => (
                <div key={r.reply_id} className="mt-2 ml-4 pl-3 border-l-2 border-amber-200">
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">{r.author_name}</span>
                    {' '}· {new Date(r.created_at).toLocaleString()}
                  </p>
                  <p className="text-sm mt-0.5">{r.text}</p>
                </div>
              ))}
              {replyTo === q.question_id ? (
                <div className="mt-2 ml-4 space-y-1">
                  <Textarea rows={2} value={replyText} onChange={e => setReplyText(e.target.value)}
                            placeholder="Write your answer…" data-testid="qa-reply-input" />
                  <div className="flex gap-1 justify-end">
                    <Button size="sm" variant="ghost" onClick={() => setReplyTo(null)}>Cancel</Button>
                    <Button size="sm" onClick={() => sendReply(q.question_id)} data-testid="qa-send-reply-btn">Reply</Button>
                  </div>
                </div>
              ) : (
                <button className="mt-1.5 ml-4 text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                        onClick={() => { setReplyTo(q.question_id); setReplyText(''); }}
                        data-testid={`qa-reply-btn-${q.question_id}`}>
                  <CornerDownRight className="w-3 h-3" /> Reply
                </button>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
